import {
  Elements,
  PaymentMethodMessagingElement,
} from "@stripe/react-stripe-js";

import { getPDPBannerDiscountText } from "@/services/HelperService";
import { getDiscountCustomColor } from "@/services/ListingHelperService";
import { Models } from "api/types";
import OfferIcon from "atoms/icon/icons/ic_offers_ol.svg";
import ShippingBoxIcon from "atoms/icon/icons/shipping_box.svg";
import { InfoBox } from "atoms/info-box/InfoBox";
import { VariantButton } from "atoms/VariantButton";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import { getPricingText } from "services/ListingHelperService";
import { currencyService } from "toolbox/CurrencyService";
import { formatCurrency } from "toolbox/Money";

export interface RetailProductPricingSectionProps {
  oneTimePurchasePriceInCents: number;
  memberPriceInCents?: number | null;
  packageType:
    | "Treatment"
    | "Package"
    | "Product"
    | "ProductFromExternalSource"
    | "Membership";
  affirmEnabled: boolean;
  isGuest: boolean;
  productDiscount?: Models<"ShopProductDiscountModel">;
  listings?: Models<"ListingModel">[];
  customShopCategories?: Models<"CategoryListingsResult">[];
  productVariants?: Models<"DisplayShopRetailProductVariant">[];
  selectedVariantId?: string;
  onVariantSelect?: (variantId: string) => void;
  shippingSubsidyMinAmountInCents?: number | null;
  productName?: string;
}

export const RetailProductPricingSection = ({
  oneTimePurchasePriceInCents,
  memberPriceInCents,
  packageType,
  affirmEnabled,
  isGuest,
  productDiscount,
  listings,
  customShopCategories,
  productVariants,
  selectedVariantId,
  onVariantSelect,
  shippingSubsidyMinAmountInCents,
  productName,
}: RetailProductPricingSectionProps) => {
  const stripeKey = useStripePublishableKey();
  const stripePromise = stripeLoader.getStripe(stripeKey);
  const currency = currencyService.getCurrency();

  const discountText =
    productDiscount &&
    listings &&
    getPDPBannerDiscountText(productDiscount, packageType, listings);

  const discountColor = getDiscountCustomColor(
    productDiscount,
    customShopCategories
  );

  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-row items-center justify-center gap-2 pb-1 text-center text-bold1">
        <div className="flex items-center gap-1.5">
          <span className="text-secondary">
            {getPricingText(
              false,
              oneTimePurchasePriceInCents,
              "product",
              "Product"
            )}
          </span>
          {memberPriceInCents != null &&
            memberPriceInCents !== oneTimePurchasePriceInCents && (
              <>
                <span className="text-neutral-300">|</span>
                <span className="font-semibold">
                  {formatCurrency(memberPriceInCents)}{" "}
                  <span className="font-normal">Member</span>
                </span>
              </>
            )}
        </div>
      </div>

      {affirmEnabled && (
        <div className="mb-2">
          <Elements
            stripe={stripePromise}
            options={{
              appearance: {
                variables: {
                  fontSizeBase: "12px",
                  colorText: "#000000",
                },
                rules: {
                  ".PaymentMethodMessaging": {
                    textAlign: "center",
                  },
                },
              },
            }}
          >
            <PaymentMethodMessagingElement
              className={
                oneTimePurchasePriceInCents > 5000
                  ? "rounded-xl bg-background-two py-1"
                  : ""
              }
              options={{
                amount: oneTimePurchasePriceInCents,
                currency: currency.code as "USD" | "CAD",
                paymentMethodTypes: ["affirm"],
                countryCode: currency.code === "CAD" ? "CA" : "US",
              }}
            />
          </Elements>
        </div>
      )}

      {productVariants && productVariants.length > 0 && (
        <div className="w-[375px] py-2">
          <div className="flex flex-row items-center justify-center px-8 text-body2">
            <div className="flex min-w-0 items-center gap-2 overflow-x-auto text-body2 scrollbar-hide">
              {productVariants.map((variant) => (
                <VariantButton
                  key={variant.id}
                  variant={variant}
                  isSelected={selectedVariantId === variant.id}
                  onSelect={() => onVariantSelect?.(variant.id)}
                  productName={productName}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mb-2">
        {packageType === "ProductFromExternalSource" ? (
          <div className="flex flex-row items-center justify-center">
            <div className="-ml-6 flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint">
              <ShippingBoxIcon className="h-6 w-6 text-brand-color" />
            </div>
            <div className="ml-3 flex flex-col items-center">
              {shippingSubsidyMinAmountInCents != null &&
                shippingSubsidyMinAmountInCents > 0 && (
                  <div className="mt-2 text-center text-chip uppercase">
                    Free standard shipping over{" "}
                    {formatCurrency(shippingSubsidyMinAmountInCents, 0)}
                  </div>
                )}
              <div className="flex flex-row items-center gap-2 pt-1">
                <div className="text-center text-body3">
                  Arrives in 3-5 business days
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-2 text-center text-chip uppercase">
            Available for in-store pickup
          </div>
        )}
      </div>

      {discountText && !isGuest && (
        <div className="px-2">
          <InfoBox
            text={discountText}
            type="discount"
            leftIcon={OfferIcon}
            customColor={discountColor ?? undefined}
          />
        </div>
      )}
    </div>
  );
};
